// uiarc: segmented-control (https://uiarc.dev/r/segmented-control.json) — Base UI `ToggleGroup` üzerine, Tailwind'e
// çevrildi. Tek bir seçili yüzey (motion `layoutId`) seçenekler arasında yayla (`morph`) kayar ve etiketlerin
// altından geçer; seçenekler ölçeklenmez/ağırlık değiştirmez, yalnız etiket rengi izler. Seçenekler kapsayıcıdan
// genişse iz içinde yatay kayar, kenarlar yalnız taşan tarafta solar. Ok tuşları/Home/End Base UI'den gelir.
// Eski `variant`/`spacing` prop'ları artık görünümü değiştirmez (hep uiarc segmented); `size="sm"` uiarc'ta yok,
// varsayılanla aynı (36 px); `lg` 44 px.
import * as React from 'react'
import { Toggle as TogglePrimitive } from '@base-ui/react/toggle'
import { ToggleGroup as ToggleGroupPrimitive } from '@base-ui/react/toggle-group'
import { LayoutGroup, motion, useReducedMotion } from 'motion/react'
import type { VariantProps } from 'class-variance-authority'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

import type { toggleVariants } from '@/components/ui/toggle'

const ToggleGroupContext = React.createContext<{
  size?: VariantProps<typeof toggleVariants>['size']
  orientation?: 'horizontal' | 'vertical'
}>({ size: 'default', orientation: 'horizontal' })

function ToggleGroup({
  className,
  size,
  orientation = 'horizontal',
  children,
  // Eski API: görünüme etkisi yok (segmented tek görünüm).
  variant: _variant,
  spacing: _spacing,
  ...props
}: ToggleGroupPrimitive.Props &
  VariantProps<typeof toggleVariants> & {
    spacing?: number
    orientation?: 'horizontal' | 'vertical'
  }) {
  const id = React.useId()
  const track = React.useRef<HTMLDivElement>(null)
  // Seçenekler kapsayıcıdan genişse iz kayar; kenarlar yalnız daha fazlası olan tarafta solar.
  React.useLayoutEffect(() => {
    const node = track.current
    if (!node) return
    const edges = () => {
      const rest = node.scrollWidth - node.clientWidth - node.scrollLeft
      node.toggleAttribute('data-fade-start', node.scrollLeft > 1)
      node.toggleAttribute('data-fade-end', rest > 1)
    }
    edges()
    node.addEventListener('scroll', edges, { passive: true })
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(edges)
    observer?.observe(node)
    return () => {
      node.removeEventListener('scroll', edges)
      observer?.disconnect()
    }
  }, [])
  // Basılı seçenek görünüme kaydırılır (ilk seferde animasyonsuz).
  const reduced = useReducedMotion()
  const first = React.useRef(true)
  React.useEffect(() => {
    const node = track.current
    if (!node) return
    const reveal = () => {
      const button = node.querySelector<HTMLElement>('[data-pressed]')
      if (!button || node.scrollWidth <= node.clientWidth) return
      const room = 20
      const start = button.offsetLeft - room
      const end = button.offsetLeft + button.offsetWidth + room - node.clientWidth
      const left = node.scrollLeft > start ? start : node.scrollLeft < end ? end : node.scrollLeft
      if (left !== node.scrollLeft)
        node.scrollTo({
          left: Math.max(0, left),
          behavior: first.current || reduced ? 'auto' : 'smooth',
        })
    }
    const observer = new MutationObserver(reveal)
    observer.observe(node, { attributes: true, attributeFilter: ['data-pressed'], subtree: true })
    reveal()
    first.current = false
    return () => observer.disconnect()
  }, [reduced])

  return (
    <div
      data-slot="toggle-group-root"
      className={cn(
        'border-border rounded-control bg-surface-muted inline-flex max-w-full min-w-0 shrink border',
        orientation === 'vertical' && 'flex-col',
        className,
      )}
    >
      <LayoutGroup id={id}>
        <ToggleGroupPrimitive
          ref={track}
          data-slot="toggle-group"
          data-size={size}
          data-orientation={orientation}
          orientation={orientation}
          className={cn(
            'group/toggle-group isolate flex min-w-0 [scrollbar-width:none] gap-0.5 overflow-x-auto overscroll-x-contain rounded-[inherit] p-[3px] [&::-webkit-scrollbar]:hidden',
            '[--fade-end:0px] [--fade-start:0px] data-fade-end:[--fade-end:20px] data-fade-start:[--fade-start:20px]',
            'mask-[linear-gradient(to_right,transparent,#000_var(--fade-start),#000_calc(100%-var(--fade-end)),transparent)]',
            orientation === 'vertical' && 'flex-col items-stretch overflow-x-visible',
          )}
          {...props}
        >
          <ToggleGroupContext.Provider value={{ size, orientation }}>
            {children}
          </ToggleGroupContext.Provider>
        </ToggleGroupPrimitive>
      </LayoutGroup>
    </div>
  )
}

function ToggleGroupItem({
  className,
  children,
  size = 'default',
  variant: _variant,
  ...props
}: TogglePrimitive.Props & VariantProps<typeof toggleVariants>) {
  const context = React.useContext(ToggleGroupContext)
  const reduced = useReducedMotion()
  const resolved = context.size || size
  return (
    <TogglePrimitive
      data-slot="toggle-group-item"
      data-size={resolved}
      className={cn(
        // Seçenekler ölçeklenmez; yalnız renk izler. Seçili yüzey etiketin arkasından süzülür.
        "text-text-muted hover:text-foreground ease-standard active:text-foreground data-pressed:text-foreground relative flex-none cursor-pointer rounded-[calc(var(--radius-control)-4px)] border-0 bg-transparent px-3 text-sm font-medium whitespace-nowrap transition-colors duration-(--duration-fast) select-none [-webkit-tap-highlight-color:transparent] disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none max-[380px]:px-2.5 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        resolved === 'lg' ? 'min-h-(--control-height-md)' : 'min-h-(--control-height-sm)',
        className,
      )}
      render={(renderProps, state) => (
        <button {...renderProps}>
          {state.pressed && (
            <motion.span
              layoutId="selection"
              transition={reduced ? { duration: 0 } : motionTokens.spring.morph}
              aria-hidden="true"
              data-slot="toggle-group-selection"
              className="border-border bg-surface shadow-resting absolute inset-0 -z-10 rounded-[inherit] border"
            />
          )}
          <span className="relative z-1 inline-flex items-center justify-center gap-1.5">
            {renderProps.children}
          </span>
        </button>
      )}
      {...props}
    >
      {children}
    </TogglePrimitive>
  )
}

export { ToggleGroup, ToggleGroupItem }
